import { memo } from "react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import type { ClickUpChecklistItem, FormattedMeetingNotes } from "@/types/fireflies";
import type { ClickUpMember } from "@/types/clickup";
import { getMemberDisplayName } from "@/lib/clickup/assigneeRouting";

export function formatChecklistLine(item: ClickUpChecklistItem): string {
  const action = item.action.endsWith(".") ? item.action : `${item.action}.`;
  if (item.dueDate?.trim()) {
    return `${action} - ${item.dueDate.trim()}`;
  }
  return action;
}

type FirefliesClickUpPreviewPanelProps = {
  notes: FormattedMeetingNotes;
  editableTitle: string;
  onTitleChange: (title: string) => void;
  members: ClickUpMember[];
  membersLoading?: boolean;
  assigneeId: number | null;
  suggestedAssigneeId: number | null;
  assigneeReason?: string;
  onAssigneeChange: (assigneeId: number | null) => void;
};

export const FirefliesClickUpPreviewPanel = memo(function FirefliesClickUpPreviewPanel({
  notes,
  editableTitle,
  onTitleChange,
  members,
  membersLoading,
  assigneeId,
  suggestedAssigneeId,
  assigneeReason,
  onAssigneeChange,
}: FirefliesClickUpPreviewPanelProps) {
  const effectiveAssignee = assigneeId ?? suggestedAssigneeId;
  const isManualOverride =
    assigneeId != null &&
    suggestedAssigneeId != null &&
    assigneeId !== suggestedAssigneeId;

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <label className="text-sm font-medium">Parent task title</label>
        <Input
          value={editableTitle}
          onChange={(e) => onTitleChange(e.target.value)}
          placeholder="Parent task title"
        />
        <p className="text-xs text-muted-foreground">
          Format: [Primary Person or Company] - [Main Topic] - [Meeting Date and Time]
        </p>
      </div>

      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <label className="text-sm font-medium">Assignee</label>
          {assigneeReason && (
            <Badge variant="secondary" className="text-xs font-normal">
              Suggested: {assigneeReason}
            </Badge>
          )}
          {isManualOverride && (
            <Badge variant="outline" className="text-xs font-normal">
              Manual override
            </Badge>
          )}
        </div>
        <Select
          value={effectiveAssignee != null ? String(effectiveAssignee) : ""}
          onValueChange={(v) => onAssigneeChange(v ? parseInt(v, 10) : null)}
          disabled={membersLoading}
        >
          <SelectTrigger>
            <SelectValue placeholder={membersLoading ? "Loading members…" : "Select assignee"} />
          </SelectTrigger>
          <SelectContent>
            {members.map((member) => (
              <SelectItem key={member.id} value={String(member.id)}>
                {member.username || getMemberDisplayName(member.id)}
                {member.email ? ` (${member.email})` : ""}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <label className="text-sm font-medium">Parent task description</label>
        <Textarea
          readOnly
          value={notes.parentTaskDescription}
          className="min-h-[200px] font-mono text-sm resize-none"
        />
      </div>

      <div className="space-y-2">
        <label className="text-sm font-medium">Checklist (max 5 action items)</label>
        {notes.checklistItems.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No hard action items identified in this meeting.
          </p>
        ) : (
          <ul className="rounded-md border divide-y text-sm">
            {notes.checklistItems.map((item, index) => (
              <li key={index} className="px-3 py-2 font-mono">
                {formatChecklistLine(item)}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
});
